import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";

export default function PreviousDebtDialog({ customer, onDone }) {
  const [ledger, setLedger] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const skipRef = useRef(null);
  const addRef = useRef(null);
  const navigateChoices = (event) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault(); event.stopPropagation();
    const choices = [skipRef.current, addRef.current].filter(button => button && !button.disabled);
    if (!choices.length) return;
    const current = choices.indexOf(document.activeElement);
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
    choices[current < 0 ? 0 : (current + step + choices.length) % choices.length].focus();
  };
  useEffect(() => {
    if (!customer) return;
    let active = true;
    setLedger(null); setError("");
    api.get(`/piutang/${customer.id}/outstanding`).then(({ data }) => {
      if (!active) return;
      if (!data.debt && !data.reason) onDone([]);
      else setLedger(data);
    }).catch(e => { if (active) setError(apiError(e)); });
    return () => { active = false; };
  }, [customer, attempt, onDone]);

  return <Dialog open={!!customer} onOpenChange={open => { if (!open) onDone([]); }}>
    <DialogContent data-testid="previous-debt-dialog" onKeyDown={navigateChoices}
      onOpenAutoFocus={event => { event.preventDefault(); skipRef.current?.focus(); }}
      className="bg-ui-card border-ui-line text-ui-text max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Piutang sebelumnya</DialogTitle>
        <DialogDescription>Periksa pelanggan sebelum menambahkan piutang ke pembayaran. Gunakan tombol panah untuk berpindah pilihan, lalu Enter.</DialogDescription>
      </DialogHeader>
      <div className="rounded-lg border border-brand/30 bg-brand-soft p-3">
        <div className="text-xs text-ui-muted">Nama pelanggan</div>
        <div data-testid="previous-debt-customer-name" className="mt-1 text-xl font-bold text-ui-text break-words">
          {ledger && ledger.customer_id === customer?.id ? ledger.customer_name : customer?.name}
        </div>
        {customer?.phone && <div data-testid="previous-debt-customer-phone" className="mt-1 text-sm text-ui-muted break-words">{customer.phone}</div>}
      </div>
      {!ledger && !error && <p className="text-sm text-ui-muted">Memeriksa sisa piutang…</p>}
      {error && <div role="alert" className="text-sm text-danger-readable">{error} <Button variant="outline" onClick={() => setAttempt(v => v + 1)}>Coba lagi</Button></div>}
      {ledger && <>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {ledger.items.map(debt => <div key={debt.invoice_id} className="flex justify-between gap-3 text-sm rounded-lg bg-ui-soft p-3">
            <span title={debt.invoice_no}>Piutang sebelumnya ({debt.invoice_no.slice(-4)})</span><strong className="font-mono whitespace-nowrap">{rupiah(debt.amount)}</strong>
          </div>)}
        </div>
        <div className="flex justify-between font-bold"><span>Total piutang</span><span>{rupiah(ledger.debt)}</span></div>
        {ledger.reason ? <p role="alert" className="text-sm text-danger-readable">{ledger.reason}</p> : <p className="text-xs text-ui-muted">Piutang baru dilunasi setelah pembayaran berhasil. Diskon belanja tidak mengurangi piutang sebelumnya.</p>}
      </>}
      <DialogFooter>
        <Button ref={skipRef} data-testid="previous-debt-skip" variant="outline" className="focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" onClick={() => onDone([])}>Tidak sekarang</Button>
        <Button ref={addRef} data-testid="previous-debt-add" className="focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" disabled={!ledger?.items.length || !!ledger?.reason} onClick={() => onDone(ledger.items)}>Tambahkan</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
